import CloseIcon from '@mui/icons-material/Close'
import { IconButton } from '@mui/material'
import type {
  AlertProps,
  ButtonProps,
  IconButtonProps,
  SnackbarOrigin,
  SnackbarProps,
} from '@mui/material'
import type { ReactNode } from 'react'
import {
  SnackbarActionButton,
  StyledAlert,
  StyledSnackbar,
} from './PersistentSnackbar.styles'

interface PersistentSnackbarProps
  extends Omit<
    SnackbarProps,
    'action' | 'children' | 'message' | 'onClose' | 'open'
  > {
  open: boolean
  message: ReactNode
  actionLabel?: ReactNode
  onAction?: ButtonProps['onClick']
  actionButtonProps?: Omit<ButtonProps, 'children' | 'onClick'>
  onClose?: () => void
  closeButtonProps?: Omit<
    IconButtonProps,
    'aria-label' | 'children' | 'onClick'
  >
  severity?: AlertProps['severity']
  variant?: AlertProps['variant']
  anchorOrigin?: SnackbarOrigin
  alertProps?: Omit<
    AlertProps,
    'action' | 'children' | 'onClose' | 'severity' | 'variant'
  >
  alertBackgroundColor?: string
  stacked?: boolean
}

const PersistentSnackbar = ({
  open,
  message,
  actionLabel,
  onAction,
  actionButtonProps,
  onClose,
  closeButtonProps,
  severity = 'info',
  variant = 'filled',
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  alertProps,
  alertBackgroundColor,
  stacked,
  ...snackbarProps
}: PersistentSnackbarProps) => {
  const hasAction = actionLabel !== undefined && onAction
  const hasAlertActions = hasAction || onClose

  return (
    <StyledSnackbar
      {...snackbarProps}
      open={open}
      anchorOrigin={anchorOrigin}
      stacked={stacked}
    >
      <StyledAlert
        {...alertProps}
        backgroundColor={alertBackgroundColor}
        severity={severity}
        variant={variant}
        action={
          hasAlertActions ? (
            <>
              {hasAction && (
                <SnackbarActionButton
                  color="inherit"
                  {...actionButtonProps}
                  onClick={onAction}
                >
                  {actionLabel}
                </SnackbarActionButton>
              )}

              {onClose && (
                <IconButton
                  color="inherit"
                  size="small"
                  {...closeButtonProps}
                  onClick={onClose}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </>
          ) : undefined
        }
      >
        {message}
      </StyledAlert>
    </StyledSnackbar>
  )
}

export default PersistentSnackbar
